import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Key, Plus, Info, GripVertical, Shield, Bot } from "lucide-react";
import { Question } from "@/types/theme";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ValidationSettings } from "./ValidationSettings";
import { ButtonOptionInput } from "./ButtonOptionInput";
import { QuestionTextInput } from "./QuestionTextInput";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

interface SortableQuestionItemProps {
  question: Question;
  index: number;
  handleUpdateQuestion: (questionId: string, updates: Partial<Question>) => void | Promise<void>;
  handleDeleteQuestion: (questionId: string) => void | Promise<void>;
  questionTypes: ReadonlyArray<{ readonly value: string; readonly label: string; }>;
  handleAddOption: (questionId: string) => void;
  handleUpdateOption: (questionId: string, optionIndex: number, value: string) => void;
  handleDeleteOption: (questionId: string, optionIndex: number) => void;
  chatbotId?: string;
}

// Constants to avoid recreating on each render
const VALID_QUESTION_TYPES = ['text', 'number', 'select', 'boolean', 'button', 'no_response'] as const;

export const SortableQuestionItem = ({
  question,
  index,
  handleUpdateQuestion,
  handleDeleteQuestion,
  questionTypes,
  handleAddOption,
  handleUpdateOption,
  handleDeleteOption,
  chatbotId
}: SortableQuestionItemProps) => {
  // State for shortname validation error
  const [shortnameError, setShortnameError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [showAIResponse, setShowAIResponse] = useState(false);
  const [localQuestion, setLocalQuestion] = useState(question);

  // Track inputs separately to prevent UI flickering
  const [questionType, setQuestionType] = useState<Question['type']>(question.type || 'text');
  const [shortname, setShortname] = useState(question.shortname || '');

  // Use ref to track the most recent shortname edit
  const pendingShortnameRef = useRef<string | null>(null);

  // Cache JSON comparison to avoid re-stringifying on every render
  const prevQuestionRef = useRef<string>('');

  // Memoized drag configuration to avoid recreating object
  const sortableConfig = useMemo(() => ({
    id: question.id,
    transition: {
      duration: 150,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    }
  }), [question.id]);

  // Update local state when props change - optimized with ref comparison
  useEffect(() => {
    const questionJson = JSON.stringify(question);

    // Skip if question hasn't actually changed
    if (questionJson === prevQuestionRef.current) {
      return;
    }

    prevQuestionRef.current = questionJson;

    // Track if any field is currently being edited to prevent unnecessary updates
    const activeElement = document.activeElement;
    const isEditingType = activeElement?.id?.includes('question-type');
    const isEditingShortname = activeElement?.id?.includes('shortname');

    // Update local question model
    setLocalQuestion(question);

    // Type field sync
    if (question.type !== questionType && !isEditingType) {
      setQuestionType(question.type || 'text');
    }

    // Shortname field sync - prevent updating while user is editing or if there's a pending edit
    if (question.shortname !== shortname && !isEditingShortname && pendingShortnameRef.current === null) {
      setShortname(question.shortname || '');
    }
  }, [question, questionType, shortname]);

  // Optimized event handlers with useCallback to prevent child re-renders
  const handleTypeChange = useCallback((value: string) => {
    // Validate that the value is one of the allowed question types
    if (!VALID_QUESTION_TYPES.includes(value as any)) {
      console.error(`Invalid question type: ${value}`);
      return;
    }

    const typedValue = value as Question['type'];
    setQuestionType(typedValue);

    // Only update if the value has changed
    if (typedValue !== question.type) {
      const updatedQuestion = { ...localQuestion, type: typedValue };
      setLocalQuestion(updatedQuestion);
      handleUpdateQuestion(question.id, { type: typedValue });
    }
  }, [question.type, question.id, localQuestion, handleUpdateQuestion]);

  const handleShortnameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setShortname(value);
    pendingShortnameRef.current = value;
  }, []);

  const handleShortnameBlur = useCallback(() => {
    const latestShortname = pendingShortnameRef.current;

    // Reset any previous errors
    setShortnameError(null);

    // Only update if there's an actual change and we have a value in the ref
    if (latestShortname !== null && latestShortname !== localQuestion.shortname) {
      // Check if shortname is empty
      if (latestShortname.trim() === '') {
        const updatedQuestion = { ...localQuestion, shortname: '' };
        setLocalQuestion(updatedQuestion);
        setShortname('');
        handleUpdateQuestion(question.id, { shortname: '' });
        pendingShortnameRef.current = null;
        return;
      }

      // Optimized duplicate check - use more efficient query
      const allShortnameInputs = document.querySelectorAll<HTMLInputElement>('[id^="shortname-"]');
      const isDuplicate = Array.from(allShortnameInputs).some(input => {
        const questionId = input.id.replace('shortname-', '');
        return questionId !== question.id && input.value.trim() === latestShortname.trim();
      });

      if (isDuplicate) {
        setShortnameError(`Duplicate shortname "${latestShortname}" found. Each question must have a unique shortname.`);
        pendingShortnameRef.current = null;
        return;
      }

      // Update with new shortname
      const updatedQuestion = { ...localQuestion, shortname: latestShortname };
      setLocalQuestion(updatedQuestion);
      setShortname(latestShortname);
      handleUpdateQuestion(question.id, { shortname: latestShortname });
    }

    pendingShortnameRef.current = null;
  }, [question.id, localQuestion, handleUpdateQuestion]);

  const handleTextKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  }, []);

  // Memoized validation settings callback to prevent unnecessary re-renders
  const handleValidationUpdate = useCallback((updatedQuestion: Question) => {
    setLocalQuestion(updatedQuestion);
    handleUpdateQuestion(question.id, updatedQuestion);
  }, [question.id, handleUpdateQuestion]);

  // Memoized button handlers
  const handleToggleAdvanced = useCallback(() => {
    setShowAdvanced(prev => !prev);
  }, []);

  const handleToggleValidation = useCallback(() => {
    setShowValidation(prev => !prev);
  }, []);

  const handleToggleAIResponse = useCallback(() => {
    setShowAIResponse(prev => !prev);
  }, []);

  const handleDelete = useCallback(async () => {
    await handleDeleteQuestion(question.id);
  }, [handleDeleteQuestion, question.id]);

  const handleAddOptionClick = useCallback(() => {
    handleAddOption(question.id);
  }, [handleAddOption, question.id]);

  // Optimized button option handlers
  const handleOptionSave = useCallback((optionIndex: number, value: string) => {
    handleUpdateOption(question.id, optionIndex, value);
  }, [handleUpdateOption, question.id]);

  const handleOptionDelete = useCallback((optionIndex: number) => {
    handleDeleteOption(question.id, optionIndex);
  }, [handleDeleteOption, question.id]);

  // Function to get contextual placeholder text based on question type
  const getQuestionPlaceholder = (type: string, currentText: string) => {
    if (currentText && currentText.trim() !== "") return undefined;
    
    switch (type) {
      case "text":
        return "What would you like to ask? (e.g., 'What's your name?')";
      case "number":
        return "Ask for a number (e.g., 'How old are you?', 'What's your phone number?')";
      case "select":
        return "Ask a multiple choice question (e.g., 'Which option do you prefer?')";
      case "boolean":
        return "Ask a yes/no question (e.g., 'Are you interested in our services?')";
      case "button":
        return "Ask a question with button options (e.g., 'Which service interests you?')";
      case "no_response":
        return "Enter a message to display (e.g., 'Thanks for your interest!')";
      default:
        return "Enter your question text here...";
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(sortableConfig);

  // Memoized style object to prevent recreating on each render
  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 'auto',
    position: isDragging ? 'relative' as const : 'static' as const,
    border: isDragging ? '2px dashed #4FD1C5' : undefined,
    background: isDragging ? '#f0fdfa' : undefined,
  }), [transform, transition, isDragging]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-gray-50 p-4 rounded-lg border ${isDragging ? 'shadow-xl' : ''}`}
    >
      <div className="flex justify-between items-start gap-4">
        <div
          {...attributes}
          {...listeners}
          className="cursor-move px-2 py-2 hover:bg-gray-200 rounded-md flex items-center h-full mt-2 touch-manipulation"
          title="Drag to reorder"
        >
          <GripVertical className="h-5 w-5 text-gray-500" />
        </div>

        <div className="flex-1 space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <QuestionTextInput
                questionId={question.id}
                chatbotId={chatbotId}
                initialText={question.text}
                enableRichMedia={true}
                initialRichContent={question.rich_content}
                placeholder={getQuestionPlaceholder(questionType, question.text)}
                onSave={(text, richContent) => {
                  console.log(`Saving question text: ${text}`, richContent);
                  const updates: Partial<Question> = { text };
                  if (richContent) {
                    updates.rich_content = richContent;
                  }
                  const updatedQuestion = { ...localQuestion, ...updates };
                  setLocalQuestion(updatedQuestion);
                  handleUpdateQuestion(question.id, updates);
                }}
              />
            </div>
            <Select
              value={questionType}
              onValueChange={handleTypeChange}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {questionTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggleAdvanced}
              className={showAdvanced ? 'bg-gray-100' : ''}
              title="Advanced Settings"
            >
              <Key className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggleValidation}
              className={showValidation ? 'bg-gray-100' : ''}
              title="Validation Settings"
            >
              <Shield className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggleAIResponse}
              className={showAIResponse ? 'bg-gray-100' : ''}
              title="AI Response Settings"
            >
              <Bot className="h-4 w-4" />
            </Button>
          </div>

          {showAdvanced && (
            <div className="bg-gray-100 p-3 rounded-md">
              <Label
                htmlFor={`shortname-${question.id}`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Short Name
              </Label>
              <Input
                id={`shortname-${question.id}`}
                type="text"
                value={shortname}
                onChange={handleShortnameChange}
                onBlur={handleShortnameBlur}
                onKeyDown={handleTextKeyDown}
                className={`w-full p-2 border rounded-md text-sm ${shortnameError ? 'border-red-500' : ''}`}
                placeholder="Short name (e.g., first_name, email, etc.)"
              />
              {shortnameError ? (
                <p className="text-xs text-red-500 mt-1">{shortnameError}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  This name will be used as the field identifier when sending responses to your backend
                </p>
              )}
            </div>
          )}

          {showValidation && (
            <ValidationSettings
              question={localQuestion}
              onUpdate={handleValidationUpdate}
            />
          )}

          {showAIResponse && (
            <div className="bg-gray-100 p-4 rounded-md space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={`ai-response-${question.id}`}
                  checked={localQuestion.enable_ai_response || false}
                  onCheckedChange={(checked) => {
                    const updates = { enable_ai_response: checked as boolean };
                    const updatedQuestion = { ...localQuestion, ...updates };
                    setLocalQuestion(updatedQuestion);
                    handleUpdateQuestion(question.id, updates);
                  }}
                />
                <Label
                  htmlFor={`ai-response-${question.id}`}
                  className="text-sm font-medium cursor-pointer"
                >
                  Enable AI Response
                </Label>
              </div>
              
              {localQuestion.enable_ai_response && (
                <div className="space-y-2">
                  <Label
                    htmlFor={`ai-prompt-${question.id}`}
                    className="text-sm font-medium text-gray-700"
                  >
                    AI Response Instructions (Optional)
                  </Label>
                  <Textarea
                    id={`ai-prompt-${question.id}`}
                    value={localQuestion.ai_response_prompt || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setLocalQuestion({ ...localQuestion, ai_response_prompt: value });
                    }}
                    onBlur={() => {
                      handleUpdateQuestion(question.id, { ai_response_prompt: localQuestion.ai_response_prompt });
                    }}
                    placeholder="E.g., 'Acknowledge their answer and provide a helpful follow-up based on their response'"
                    className="w-full min-h-[80px]"
                  />
                  <p className="text-xs text-gray-500">
                    Provide specific instructions for how the AI should respond to the user's answer. 
                    If left blank, the AI will generate a contextual response based on the conversation.
                  </p>
                </div>
              )}
              
              <div className="bg-blue-50 text-blue-800 p-3 rounded-md border border-blue-200">
                <div className="flex items-start gap-2">
                  <Bot className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium">AI Response Feature</p>
                    <p className="text-sm text-blue-700 mt-1">
                      When enabled, the AI will automatically generate a response after the user answers this question. 
                      This helps create a more conversational and engaging experience.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {questionType === 'no_response' && (
            <div className="space-y-2 mt-2">
              <div className="bg-blue-50 text-blue-800 p-3 rounded-md border border-blue-200">
                <div className="flex items-start gap-2">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium">No Response Question</p>
                    <p className="text-sm text-blue-700 mt-1">
                      This question will be displayed to the user, but won't require any response.
                      The conversation will automatically proceed to the next question after a short delay.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {questionType === 'button' && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium text-gray-700">Button Options</label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddOptionClick}
                  className="flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add Option
                </Button>
              </div>
              <div className="space-y-2">
                {(question.options || []).map((option, optionIndex) => (
                  <ButtonOptionInput
                    key={optionIndex}
                    option={option}
                    optionIndex={optionIndex}
                    onSave={handleOptionSave}
                    onDelete={handleOptionDelete}
                  />
                ))}
              </div>
            </div>
          )}


        </div>
        <Button
          variant="destructive"
          size="icon"
          onClick={handleDelete}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
